import React from 'react';
import Markdown from 'markdown-to-jsx';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import LockClosed from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/objects/components/lock-closed/index.tsx';
import Chat from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/other/components/chat/index.tsx';
import Download from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/software/components/download/index.tsx';
import Settings from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/software/components/settings/index.tsx';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { Anchor } from '../../../common/ui/anchor/index.tsx';
import { TransitionHomeContentBox } from '../../../common/ui/transition-home/content-box/index.tsx';
import { TransitionHomeHeader } from '../../../common/ui/transition-home/header/index.tsx';
import { messages as stepMessages } from '../../../common/ui/transition-home/step/messages.tsx';
import taskMessages from '../../../common/utils/tasks/messages.tsx';
import { useGlobalChangeboarding } from '../../../controllers/store/global/index.tsx';
import {
	useChatToolsConnections,
	useChatToolsIntegrations,
} from '../../../services/store/chat-tools/index.tsx';
import { useIsTeamAdmin } from '../../../services/store/team-admin-status/index.tsx';
import { ConnectChatToolsTable } from './connect-chat-tools-table/index.tsx';
import { CopyIntegrationsTable } from './copy-integrations-table/index.tsx';
import { messages } from './messages.tsx';

export const ChatToolsStep = () => {
	const { formatMessage } = useIntl();
	const isPrimaryAdmin = useIsAdmin();
	const [{ isTeamAdmin }] = useIsTeamAdmin();

	const [connections] = useChatToolsConnections();
	const [integrations] = useChatToolsIntegrations();

	if (!connections.data && !isTeamAdmin) {
		throw new Error('ChatToolsStep should be rendered when chat tools connections are loaded');
	}

	const { opsgenieBaseUrl } = useGlobalChangeboarding();

	const ogTeamsUrl = opsgenieBaseUrl ? `${opsgenieBaseUrl}/teams/list` : '';
	const ogIntegrationsUrl = opsgenieBaseUrl ? `${opsgenieBaseUrl}/settings/integrations/` : '';

	return (
		<Stack space="space.250">
			<TransitionHomeHeader
				title={<FormattedMessage {...stepMessages.chatToolsTitleNonFinal} />}
				description={<FormattedMessage {...stepMessages.chatToolsDescriptionNonFinal} />}
			/>

			{/* Task One */}
			<TransitionHomeContentBox illustration={<Chat alt="" width={100} />}>
				<Stack space="space.200">
					<Heading as="h2" size="small">
						<FormattedMessage {...taskMessages.chatToolsTaskOneNonFinal} />
					</Heading>
					<Box>
						<Markdown options={{ overrides: { a: { component: Anchor } } }}>
							{formatMessage(messages.taskOneDescriptionNonFinal)}
						</Markdown>
					</Box>
				</Stack>
			</TransitionHomeContentBox>

			{/* Task Two */}
			{isPrimaryAdmin && (
				<TransitionHomeContentBox illustration={<LockClosed width={100} alt="" />}>
					<Stack space="space.200">
						<Heading as="h2" size="small">
							<FormattedMessage {...taskMessages.chatToolsTaskTwoNonFinal} />
						</Heading>
						<Box>
							<Markdown>{formatMessage(messages.taskTwoDescriptionNonFinal)}</Markdown>
						</Box>
						<Box>
							<Button
								href="/jira/settings/products/ops/jira-service-management-chatops-and-video-tools"
								target="_blank"
							>
								<FormattedMessage {...messages.taskTwoActionButtonNonFinal} />
							</Button>
						</Box>
						<Box paddingBlockStart="space.250">
							<ConnectChatToolsTable connections={connections} />
						</Box>
					</Stack>
				</TransitionHomeContentBox>
			)}

			{/* Task Three */}
			{(integrations.data?.count || 0) > 0 && (
				<TransitionHomeContentBox
					illustration={<Download alt="" width={100} />}
					footer={
						<Box paddingBlockStart="space.150">
							<CopyIntegrationsTable integrations={integrations} />
						</Box>
					}
				>
					<Stack space="space.200">
						<Heading as="h2" size="small">
							<FormattedMessage {...taskMessages.chatToolsTaskThreeNonFinal} />
						</Heading>
						<Box>
							<Markdown>{formatMessage(messages.taskThreeDescriptionNonFinal)}</Markdown>
						</Box>
						{isPrimaryAdmin && (
							<Box>
								<Button href="/jira/settings/products/ops/integrations" target="_blank">
									<FormattedMessage {...messages.taskThreeActionButtonNonFinal} />
								</Button>
							</Box>
						)}
					</Stack>
				</TransitionHomeContentBox>
			)}

			{/* Task Four */}
			<TransitionHomeContentBox illustration={<Settings width={100} alt="" />}>
				<Stack space="space.200">
					<Heading as="h2" size="small">
						<FormattedMessage {...taskMessages.chatToolsTaskFourNonFinal} />
					</Heading>
					<Box>
						<Markdown>
							{formatMessage(
								isTeamAdmin
									? messages.taskFourTeamAdminDescriptionNonFinal
									: messages.taskFourDescriptionNonFinal,
							)}
						</Markdown>
					</Box>
					<Box>
						<Button href={isTeamAdmin ? ogTeamsUrl : ogIntegrationsUrl} target="_blank">
							{formatMessage(
								isTeamAdmin
									? messages.taskFourTeamAdminActionButtonNonFinal
									: messages.taskFourActionButtonNonFinal,
							)}
						</Button>
					</Box>
				</Stack>
			</TransitionHomeContentBox>
		</Stack>
	);
};
